/**
 * IcalSection — manage Airbnb / Booking / Vrbo / Direct iCal URLs
 * for a single property. Inline form + sync action + remove URL.
 */
import { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { ArrowsClockwise, Trash, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { key: 'airbnb', label: 'Airbnb', color: 'rose' },
  { key: 'booking', label: 'Booking.com', color: 'blue' },
  { key: 'vrbo', label: 'Vrbo', color: 'amber' },
  { key: 'direct', label: 'Diretta / Altro', color: 'zinc' },
];

const COLOR_CLASS = {
  rose: 'border-rose-200 bg-rose-50 text-rose-900',
  blue: 'border-blue-200 bg-blue-50 text-blue-900',
  amber: 'border-amber-200 bg-amber-50 text-amber-900',
  zinc: 'border-zinc-200 bg-zinc-50 text-zinc-900',
};

export default function IcalSection({ property, onChanged }) {
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null); // platform key currently loading
  const icalUrls = property?.ical_urls || {};
  const lastSync = property?.ical_last_sync || {};

  const handleSync = async (platform) => {
    const url = drafts[platform] !== undefined ? drafts[platform] : icalUrls[platform];
    if (!url || !url.trim()) {
      toast.error('Incolla prima il link iCal');
      return;
    }
    setBusy(platform);
    try {
      const res = await axios.post(`${API}/ical/sync`, {
        property_id: property.id,
        platform,
        url: url.trim(),
      });
      const r = res.data;
      toast.success(
        `Sync OK: ${r.imported} nuove · ${r.updated} aggiornate · ${r.skipped_blocked} bloccate ignorate`,
      );
      setDrafts((d) => ({ ...d, [platform]: undefined }));
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sync iCal');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (platform) => {
    if (!window.confirm(`Rimuovere il calendario ${platform}? Le prenotazioni future verranno cancellate dal sistema.`)) return;
    setBusy(platform);
    try {
      await axios.delete(`${API}/ical/url`, { data: { property_id: property.id, platform } });
      toast.success('Calendario disconnesso');
      setDrafts((d) => ({ ...d, [platform]: '' }));
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rimozione');
    } finally {
      setBusy(null);
    }
  };

  const formatLastSync = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return 'pochi secondi fa';
      if (diffMin < 60) return `${diffMin} min fa`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `${diffH}h fa`;
      return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
    } catch {
      return null;
    }
  };

  return (
    <div className="border-t-2 border-dashed border-zinc-200 pt-6 mt-2" data-testid="ical-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
            Sincronizza calendari iCal
          </Label>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Incolla il link <span className="font-mono">.ics</span> di Airbnb / Booking / Vrbo. Il sistema sincronizza automaticamente ogni 6 ore e ti avvisa quando manca un contratto.
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {PLATFORMS.map((p) => {
          const saved = icalUrls[p.key];
          const draft = drafts[p.key];
          const value = draft !== undefined ? draft : saved || '';
          const synced = formatLastSync(lastSync[p.key]);
          const cls = COLOR_CLASS[p.color];
          return (
            <div key={p.key} className={`border ${cls} px-3 py-3 sm:px-4`} data-testid={`ical-row-${p.key}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black uppercase tracking-wider">{p.label}</span>
                  {saved && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 border border-emerald-200">
                      <CheckCircle size={10} weight="fill" /> Connesso
                    </span>
                  )}
                  {synced && saved && (
                    <span className="text-[10px] text-zinc-500">· sync {synced}</span>
                  )}
                </div>
                {saved && (
                  <button
                    type="button"
                    onClick={() => handleRemove(p.key)}
                    disabled={busy === p.key}
                    className="text-zinc-400 hover:text-red-500 disabled:opacity-50"
                    title="Disconnetti"
                    data-testid={`ical-remove-${p.key}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="url"
                  value={value}
                  onChange={(e) => setDrafts((d) => ({ ...d, [p.key]: e.target.value }))}
                  placeholder={
                    p.key === 'airbnb' ? 'https://www.airbnb.com/calendar/ical/…'
                    : p.key === 'booking' ? 'https://admin.booking.com/hotel/hoteladmin/ical.html?…'
                    : p.key === 'vrbo' ? 'https://www.vrbo.com/icalendar/…'
                    : 'https://… (qualsiasi link .ics)'
                  }
                  className="rounded-none bg-white text-xs sm:text-sm flex-1 font-mono"
                  data-testid={`ical-input-${p.key}`}
                />
                <Button
                  type="button"
                  onClick={() => handleSync(p.key)}
                  disabled={busy === p.key || !value}
                  className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs uppercase tracking-wider px-4 disabled:opacity-50"
                  data-testid={`ical-sync-${p.key}`}
                >
                  {busy === p.key ? '...' : <><ArrowsClockwise size={14} weight="bold" className="mr-1" /> {saved ? 'Aggiorna' : 'Connetti'}</>}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-start gap-2 mt-3 text-[11px] text-zinc-500 leading-relaxed">
        <Warning size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
        <span>
          Le date "bloccate" senza ospite (es. manutenzione, prenotazioni private) vengono ignorate automaticamente. Solo prenotazioni reali generano la segnalazione "manca contratto".
        </span>
      </div>
    </div>
  );
}
